import React from 'react';
import { formatCurrency } from '@/lib/helpers/strings';
import Areas from 'modules/geo/components/Areas';
import CountryEmojis from 'modules/geo/data-components/CountryEmojis';
import { useAuthModal } from 'modules/auth';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/lib/components/ui/card';
import { Button } from '@/lib/components/ui/button';
import { Badge } from '@/lib/components/ui/badge';
import { Euro } from 'lucide-react';
import dayjs from 'dayjs';
import { experiences } from 'modules/User/models/constants';
import { MdFolderSpecial } from 'react-icons/md';
import useUser from 'modules/User/hooks/useUser';
import router from 'next/router';
import classNames from 'classnames';
import { SearchedProfile } from '../interfaces';

type MissionsProps = React.HTMLAttributes<HTMLDivElement> & {
  missions: SearchedProfile[];
  maxItems?: number;
};

const Missions: React.FC<MissionsProps> = ({ children, className, missions, maxItems = 6, ...props }) => {
  const { toggleModal } = useAuthModal();
  const { user } = useUser();
  const [allShown, setAllShow] = React.useState(false);

  return (
    <div className={classNames('container mx-auto py-8', className)} {...props}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {missions.map((mission, index) => (
          <Card key={mission._id} className={classNames('flex flex-col justify-between', index >= maxItems && !allShown && 'hidden')}>
            <CardHeader>
              <CardTitle>
                <div className="flex items-center gap-2 justify-between mb-3">
                  <Badge variant="outline">{mission.occupation?.name}</Badge>
                  <Badge variant="outline-primary" size="lg">
                    {formatCurrency({ value: mission.bounty, currency: mission.currency })}
                  </Badge>
                </div>
                <span className="text-lg font-bold">{mission.name}</span>
              </CardTitle>
              <CardDescription>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <CountryEmojis countryIds={mission.countryIds || []} />
                  {mission.sectors?.map((sector: any) => (
                    <Badge key={sector._id} variant="outline">
                      {sector.name}
                    </Badge>
                  ))}
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="space-y-2">
                <div className="flex items-center text-gray-600">
                  <Areas items={mission.sites?.areas || []} className="!text-base" />
                </div>

                <div className="flex items-center text-gray-600">
                  <Euro className="h-4 w-4 mr-2" />
                  <span>
                    {mission.baseSalary.min.toLocaleString('fr-FR')} - {mission.baseSalary.max.toLocaleString('fr-FR')} €
                  </span>
                </div>

                <div className="flex items-center text-gray-600">
                  <MdFolderSpecial className="h-4 w-4 mr-2" />
                  <span>{(experiences as any)[mission?.experience] || mission.experience}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-end flex-col gap-0.5 items-end bg-gray-50 py-3 px-6">
              <Button
                variant="accent"
                onClick={() => {
                  if (user) {
                    router.push(`/searched-profiles/${mission._id}`);
                  } else {
                    toggleModal();
                  }
                }}
              >
                Voir la mission
              </Button>
              <div className="text-[10px] text-gray-500" suppressHydrationWarning>
                Publié(e) {dayjs(mission.publishedAt).fromNow()}
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>
      {missions.length > maxItems && (
        <div className="flex justify-center mt-12 sticky bottom-3 bg-white/80 py-2">
          <Button variant="primary" onClick={() => setAllShow(!allShown)} size="lg">
            {allShown ? (
              'Voir moins'
            ) : (
              <>
                Voir <strong>{missions.length - maxItems}</strong> autres missions
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Missions;
